// ===========================================
// Subscriptions React Query Hooks
// ===========================================
// Custom hooks for subscription management API operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  adminSubscriptionsApi,
  parentSubscriptionsApi,
  SubscriptionFilters,
  CreateSubscriptionInput,
  PauseSubscriptionInput,
  CancelSubscriptionInput,
  ChangePaymentPlanInput,
} from '../api/subscriptions.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  lists: () => [...subscriptionKeys.all, 'list'] as const,
  list: (filters?: SubscriptionFilters) => [...subscriptionKeys.lists(), filters] as const,
  details: () => [...subscriptionKeys.all, 'detail'] as const,
  detail: (id: string) => [...subscriptionKeys.details(), id] as const,
  family: (familyId: string) => [...subscriptionKeys.all, 'family', familyId] as const,
  parent: {
    all: ['parent-subscriptions'] as const,
    list: () => ['parent-subscriptions', 'list'] as const,
    detail: (id: string) => ['parent-subscriptions', 'detail', id] as const,
  },
};

// ===========================================
// ADMIN - SUBSCRIPTION QUERIES
// ===========================================

/**
 * Get all subscriptions for the school
 */
export function useAdminSubscriptions(filters?: SubscriptionFilters) {
  return useQuery({
    queryKey: subscriptionKeys.list(filters),
    queryFn: () => adminSubscriptionsApi.getAll(filters),
  });
}

/**
 * Get a single subscription by ID
 */
export function useAdminSubscription(id: string) {
  return useQuery({
    queryKey: subscriptionKeys.detail(id),
    queryFn: () => adminSubscriptionsApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Get subscriptions for a specific family
 */
export function useFamilySubscriptions(familyId: string) {
  return useQuery({
    queryKey: subscriptionKeys.family(familyId),
    queryFn: () => adminSubscriptionsApi.getByFamily(familyId),
    enabled: !!familyId,
  });
}

// ===========================================
// ADMIN - SUBSCRIPTION MUTATIONS
// ===========================================

/**
 * Create a new subscription
 */
export function useCreateSubscription() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateSubscriptionInput) => adminSubscriptionsApi.create(data),
    onSuccess: (subscription) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.family(subscription.familyId) });
      enqueueSnackbar('Subscription created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to create subscription', {
        variant: 'error',
      });
    },
  });
}

/**
 * Pause a subscription
 */
export function usePauseSubscription() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: PauseSubscriptionInput }) =>
      adminSubscriptionsApi.pause(id, data),
    onSuccess: (subscription, { id }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.family(subscription.familyId) });
      enqueueSnackbar('Subscription paused', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to pause subscription', {
        variant: 'error',
      });
    },
  });
}

/**
 * Resume a paused subscription
 */
export function useResumeSubscription() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => adminSubscriptionsApi.resume(id),
    onSuccess: (subscription, id) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.family(subscription.familyId) });
      enqueueSnackbar('Subscription resumed', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to resume subscription', {
        variant: 'error',
      });
    },
  });
}

/**
 * Cancel a subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: CancelSubscriptionInput }) =>
      adminSubscriptionsApi.cancel(id, data),
    onSuccess: (subscription, { id }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.family(subscription.familyId) });
      enqueueSnackbar('Subscription cancelled', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to cancel subscription', {
        variant: 'error',
      });
    },
  });
}

/**
 * Change the payment plan for a subscription
 */
export function useChangeSubscriptionPaymentPlan() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChangePaymentPlanInput }) =>
      adminSubscriptionsApi.changePaymentPlan(id, data),
    onSuccess: (subscription, { id }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.family(subscription.familyId) });
      enqueueSnackbar('Payment plan updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to change payment plan', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// PARENT - SUBSCRIPTION QUERIES
// ===========================================

/**
 * Get subscriptions for the parent's family
 */
export function useParentSubscriptions() {
  return useQuery({
    queryKey: subscriptionKeys.parent.list(),
    queryFn: () => parentSubscriptionsApi.getAll(),
  });
}

/**
 * Get a specific subscription
 */
export function useParentSubscription(id: string) {
  return useQuery({
    queryKey: subscriptionKeys.parent.detail(id),
    queryFn: () => parentSubscriptionsApi.getById(id),
    enabled: !!id,
  });
}

// ===========================================
// PARENT - SUBSCRIPTION MUTATIONS
// ===========================================

/**
 * Create a subscription
 */
export function useParentCreateSubscription() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: Omit<CreateSubscriptionInput, 'familyId'>) =>
      parentSubscriptionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.all });
      enqueueSnackbar('Subscription created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to create subscription', {
        variant: 'error',
      });
    },
  });
}

/**
 * Pause own subscription
 */
export function useParentPauseSubscription() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: PauseSubscriptionInput }) =>
      parentSubscriptionsApi.pause(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.detail(id) });
      enqueueSnackbar('Subscription paused', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to pause subscription', {
        variant: 'error',
      });
    },
  });
}

/**
 * Resume own subscription
 */
export function useParentResumeSubscription() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => parentSubscriptionsApi.resume(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.detail(id) });
      enqueueSnackbar('Subscription resumed', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to resume subscription', {
        variant: 'error',
      });
    },
  });
}

/**
 * Cancel own subscription
 */
export function useParentCancelSubscription() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: CancelSubscriptionInput }) =>
      parentSubscriptionsApi.cancel(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.detail(id) });
      enqueueSnackbar('Subscription cancelled', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to cancel subscription', {
        variant: 'error',
      });
    },
  });
}

/**
 * Update payment method
 */
export function useParentUpdatePaymentMethod() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, paymentMethodId }: { id: string; paymentMethodId: string }) =>
      parentSubscriptionsApi.updatePaymentMethod(id, paymentMethodId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.parent.detail(id) });
      enqueueSnackbar('Payment method updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update payment method', {
        variant: 'error',
      });
    },
  });
}

/**
 * Create a SetupIntent for adding a payment method
 */
export function useCreateSetupIntent() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => parentSubscriptionsApi.createSetupIntent(),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to setup payment method', {
        variant: 'error',
      });
    },
  });
}
